<template>
  <BonusCard
    v-if="!isExpired"
    :id="bonus.id"
    :type="bonus.type"
    :level="bonus.level"
    :timer="bonusTimer"
    :data-t="`${bonus.type}-card`"
    :highlight="highlight === bonus.type"
    :class="bonus.status"
  >
    <template v-if="bonusStepProgress" #title-append>
      <StCounter
        class="step-progress"
        size="xs"
        :label="bonusStepProgress"
        bg-color="ghost"
        color="white"
      />
    </template>
    <template #subtitle>
      <div v-if="isPercentsAmountVisible" class="amount percent">
        <span>{{ percentAmount }}</span>
      </div>
      <div v-else class="amount" :class="{ zero: isZeroAmount }">
        <span>{{ bonusAmount }}</span>
        <StIcon
          :name="currencyIcon"
          :size="16"
          :data-t-currency="currencyIcon"
        />
      </div>
    </template>

    <BonusCardBorderedContent
      v-if="content.terms.length"
      class="content"
      data-t="content"
    >
      <BonusCardTerms :list="content.terms" />
    </BonusCardBorderedContent>
    <template #actions>
      <div class="actions-container">
        <StButton
          class="button"
          :disabled="isButtonDisabled"
          :type="buttonSettigns.type"
          :loading="claimStatus === 'pending'"
          :label="buttonSettigns.label"
          :icon="buttonSettigns.icon"
          is-left-icon
          :is-pulsing="buttonSettigns.isPulsing"
          @click="handleBonusButtonClick"
        />
      </div>
    </template>
    <LottieClaimAnimation ref="claimAnimation" :is-big="isMobile" />
  </BonusCard>
</template>

<script setup lang="ts">
import LottieClaimAnimation from '@st/ui/components/LottieClaimAnimation/LottieClaimAnimation.client.vue'
import { useRegularBonus } from './useRegularBonus'
import type { RegularBonus } from '../../types'
import BonusCard from '../BonusCard/BonusCard.vue'
import BonusCardBorderedContent from '../BonusCard/parts/BonusCardBorderedContent.vue'
import BonusCardTerms from '../BonusCard/parts/BonusCardTerms.vue'

const animation =
  useTemplateRef<InstanceType<typeof LottieClaimAnimation>>('claimAnimation')

const { bonus, highlight } = defineProps<{
  bonus: RegularBonus
  highlight: string | null
}>()

const {
  handleBonusButtonClick,
  claimStatus,
  currencyIcon,
  bonusAmount,
  isZeroAmount,
  isExpired,
  bonusTimer,
  content,
  percentAmount,
  isButtonDisabled,
} = useRegularBonus(
  computed(() => bonus),
  () => animation.value?.runClaimAnimation(),
)

const { isMobile } = usePlatform()
const { t } = useI18n()
const buttonSettigns = computed(() => {
  if (bonus.type === 'reload') {
    return {
      label: t('bonuses.take'),
      isPulsing: bonus.status !== 'waitingForAccrual',
      icon: undefined,
      type: 'primary' as const,
    }
  }
  if (bonus.type === 'cashback') {
    const cashbackWithNoAmountAndWaiting =
      isZeroAmount.value && bonus.status === 'waitingForAccrual'
    return {
      isPulsing: bonus.status !== 'waitingForAccrual',
      label: cashbackWithNoAmountAndWaiting
        ? t('bonuses.placeBets')
        : t('bonuses.take'),
      icon: cashbackWithNoAmountAndWaiting
        ? ('play-solid' as const)
        : undefined,
      type: cashbackWithNoAmountAndWaiting
        ? ('ghost' as const)
        : ('primary' as const),
    }
  }

  switch (bonus.status) {
    case 'readyToClaim':
      return {
        label: t('bonuses.take'),
        icon: undefined,
        type: 'primary' as const,
        isPulsing: true,
      }
    case 'waitingForAccrual':
      return {
        label: t('bonuses.placeBets'),
        icon: 'play-solid' as const,
        type: 'ghost' as const,
      }
    default:
      return {
        label: t('bonuses.goToGame'),
        icon: 'play-solid' as const,
        type: 'ghost' as const,
      }
  }
})

const isPercentsAmountVisible = computed(
  () => percentAmount.value && isZeroAmount.value && bonus.type === 'rakeBack',
)

const bonusStepProgress = computed(() => {
  if (bonus.totalSteps && bonus.currentStep) {
    return `${bonus.currentStep}/${bonus.totalSteps}`
  }
  return ''
})
</script>

<style scoped>
.actions-container {
  position: relative;
  display: flex;
  flex: 1 1 50%;
  min-width: 0;
}

.button {
  position: relative;
  flex: 1 1 0;
  min-width: 0;
}

.amount {
  display: flex;
  gap: var(--spacing-025);
  align-items: center;

  &.zero {
    color: var(--text-secondary);
  }
}

.progress-data {
  display: flex;
  align-items: center;
  justify-content: space-between;

  font: var(--mobile-caption-1-regular);
  color: var(--text-primary);
}

.current-progress {
  display: flex;
  gap: var(--spacing-025);
  align-items: center;
}

.step-progress {
  display: inline-flex;
  margin-left: var(--spacing-050);
}
</style>
